import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import Negotiator from 'negotiator';
import { Locale } from '../../core/constants';
import { REGIONS } from '../../core/japaneseRegions';
import { getAllHotels } from '../../core/hotel/getAllHotels';
import { getAllCities } from '../../core/city/getAllCities';
import convertCityPrefecture from '../../core/helpers/convertCityPrefecture';

const BASE_URL = 'https://tabist.co.jp';

function generateSiteMap(
  links: { locale: string; path: string; priority: string; updatedAt?: string }[]
) {
  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xhtml="http://www.w3.org/1999/xhtml">
${links
  .map((link) => {
    return `
<url>
  <loc>${`${BASE_URL}${link?.locale !== Locale.JAPANESE ? `/${link?.locale}` : ''}${
    link?.path
  }`}</loc>
  ${link?.updatedAt ? `<lastmod>${link?.updatedAt}</lastmod>` : ''}
  <priority>${link?.priority}</priority>
</url>
`;
  })
  .join('')}
</urlset>`;
}

const SiteMap = () => {
  return null;
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const headers = { 'accept-language': (context.query.locale as string).split('.')[0] as string };
  const negotiator = new Negotiator({ headers });
  const locale = negotiator.language(Object.values(Locale)) as string;

  // Currently NextJS does not support dynamic routing with file extension suffix
  if ((context.query.locale as string).endsWith('.xml') && locale) {
    const fields = ['property_id', 'hotel_name', 'updated_at'];

    const [{ hotels }, { cities }] = await Promise.all([
      getAllHotels(fields, Locale.JAPANESE),
      getAllCities(),
    ]);

    const links: { locale: string; path: string; priority: string; updatedAt?: string }[] = [];

    /**
     * Add urls:
     * * /search/region/{region name}
     * * /search/prefecture/{prefecture name}
     */
    const searchLinks = REGIONS.reduce((accumulator: string[], region) => {
      accumulator.push(`/search/region/${region.path}`);
      region.prefectures.forEach((prefecture) => {
        accumulator.push(`/search/prefecture/${prefecture.path}`);
      });

      return accumulator;
    }, []);

    links.push({ locale, path: '', priority: '1.0' });

    /**
     * Add urls:
     * * /search/region/{region name}
     * * /search/prefecture/{prefecture name}
     */
    searchLinks.forEach((link) => {
      links.push({ locale, path: link, priority: '0.7' });
    });

    cities.forEach((city) => {
      /**
       * Add urls:
       * * /search/city/{city_en_prefecture name}_{city_en_name}
       */
      links.push({
        locale,
        path: `/search/city/${convertCityPrefecture(
          city?.cityName?.en,
          city?.prefectureName?.en
        ).join('_')}`,
        priority: '0.7',
      });
    });

    /**
     * Add urls:
     * * /h/{hotel id}
     */
    hotels.forEach((hotel) => {
      links.push({ locale, path: `/h/${hotel?.id}`, priority: '0.5', updatedAt: hotel?.updatedAt });
    });

    // We generate the XML sitemap with the posts data
    if (
      process.env.NEXT_PUBLIC_VERCEL_ENV === 'production' ||
      process.env.VERCEL_GIT_COMMIT_REF === 'stg'
    ) {
      const sitemap = generateSiteMap(links);

      context.res.setHeader('Content-Type', 'application/xml');
      // we send the XML to the browser
      context.res.write(sitemap);
      context.res.end();
    }
  }

  return {
    props: {},
  };
};

export default SiteMap;
